import { memo, useState } from 'react';
import { Box, Modal } from '@mui/material';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { Text, fontWeight, textLevel } from '../../../../packages/ui/atoms/typography';
import { getProfile, selectMemberInsurance } from '../../../../redux/modules/profile/selectors';
import { useStyles } from './AppointmentInfo.styles';
import { Icons, colors } from '../../../../packages';

/**
 * AppointmentInfo Component
 * Displays member basic information in a horizontal grid with ID cards modal
 */
const AppointmentInfo = memo(({ appointment, isUpcomingAppointment = false }) => {
  const classes = useStyles({ isUpcomingAppointment });
  const { demographicDetails } = useSelector(getProfile);
  const { insurance } = useSelector(selectMemberInsurance);
  const [idCardsModalOpen, setIdCardsModalOpen] = useState(false);

  const calculateAge = dob => {
    if (!dob) return 'N/A';
    return dayjs().diff(dayjs(dob), 'year');
  };

  const hasIdCards = demographicDetails?.idCardFront || demographicDetails?.idCardBack;

  const infoData = !isUpcomingAppointment
    ? [
        { label: 'Age', value: calculateAge(demographicDetails?.dateOfBirth) },
        { label: 'Sex', value: demographicDetails?.sex || 'N/A' },
        { label: 'State', value: appointment?.patientState || 'N/A' },
        {
          label: 'DOB',
          value: demographicDetails?.dateOfBirth
            ? dayjs(demographicDetails.dateOfBirth).format('M.D.YYYY')
            : 'N/A',
        },
        { label: 'Insurance', value: insurance?.currentInsurance?.name || 'N/A' },
        {
          label: 'ID cards',
          value: hasIdCards ? 'View ID cards' : 'Not Uploaded',
          isLink: hasIdCards,
          onClick: () => hasIdCards && setIdCardsModalOpen(true),
        },
      ]
    : [
        { label: 'State', value: appointment?.patientState || 'N/A' },
        {
          label: 'Payment',
          value: demographicDetails?.preferredPaymentMethod === 'INSURANCE' ? 'Insurance' : 'Cash',
        },
        { label: 'Insurance', value: insurance?.currentInsurance?.name || 'N/A' },
        {
          label: 'VBC',
          value: !demographicDetails?.vbcFlag ? (
            <Icons glyph="checkmark" color={colors.success500} />
          ) : (
            <Icons glyph="x-circle-outlined" color={colors.destructive600} />
          ),
        },
      ];

  return (
    <>
      <Box className={classes.waitingRoomInfo}>
        {infoData.map((item, index) => (
          <Box key={index} className={classes.infoRow}>
            <Text className={classes.infoLabel}>{item.label}</Text>
            {item.isLink ? (
              <Text className={classes.infoLink} onClick={item.onClick}>
                {item.value}
              </Text>
            ) : (
              <Text className={classes.infoValue}>{item.value}</Text>
            )}
          </Box>
        ))}
      </Box>

      {/* ID Cards Modal */}
      <Modal open={idCardsModalOpen} onClose={() => setIdCardsModalOpen(false)}>
        <Box className={classes.modalOverlay}>
          <Box className={classes.modalContent}>
            <Box className={classes.modalHeader}>
              <Text level={textLevel.L} weight={fontWeight.SEMI_BOLD}>
                ID Cards
              </Text>
              <Icons glyph="close" onClick={() => setIdCardsModalOpen(false)} color={colors.neutral400} />
            </Box>
            <Box className={classes.modalBody}>
              <Box display="flex" gap={3} p={3}>
                {demographicDetails?.idCardFront ? (
                  <img src={demographicDetails.idCardFront} alt="id card front" className={classes.idCard} />
                ) : (
                  <Box
                    className={classes.idCard}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    border={`1px solid ${colors.neutral100}`}
                    textAlign="center"
                  >
                    Front of ID - Not Uploaded
                  </Box>
                )}
                {demographicDetails?.idCardBack ? (
                  <img src={demographicDetails.idCardBack} alt="id card back" className={classes.idCard} />
                ) : (
                  <Box
                    className={classes.idCard}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    border={`1px solid ${colors.neutral100}`}
                    textAlign="center"
                  >
                    Back of ID - Not Uploaded
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
});

AppointmentInfo.displayName = 'AppointmentInfo';

export { AppointmentInfo };
